import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { auth, RecaptchaVerifier } from "../firebase";
import { signInWithPhoneNumber, signOut, onAuthStateChanged } from "firebase/auth";
import PropTypes from "prop-types";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

	useEffect(() => {
		// Initialize reCAPTCHA verifier
		const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
			size: "invisible",
			callback: () => {
				// reCAPTCHA solved, allow signInWithPhoneNumber.
			},
		});
		setRecaptchaVerifier(verifier);

		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser);
			setLoading(false);
		});

		return () => {
			unsubscribe();
			if (recaptchaVerifier) {
				recaptchaVerifier.clear();
			}
		};
	}, []);

	const signInWithPhone = async (phoneNumber) => {
		try {
			const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
			return confirmationResult;
		} catch (error) {
			console.error("Error sending SMS:", error);
			throw error;
		}
	};

	const verifyCode = async (confirmationResult, code) => {
		try {
			const result = await confirmationResult.confirm(code);
			setUser(result.user);
			return result.user;
		} catch (error) {
			console.error("Error verifying code:", error);
			throw error;
		}
	};

	const logout = async () => {
		try {
			await signOut(auth);
			setUser(null);
		} catch (error) {
			console.error("Error signing out:", error);
			throw error;
		}
	};

	const value = useMemo(
		() => ({
			user,
			loading,
			signInWithPhone,
			verifyCode,
			logout,
		}),
		[user, loading]
	);

	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
			<div id="recaptcha-container"></div>
		</AuthContext.Provider>
	);
};

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
